export type ProposedPart = {
  description?: string;
  quantity?: string;
  source?: string;
  suggestedSourceType?: "in-house-catalog" | "external-supplier" | "manual-review";
  suggestedSourceName?: string;
};

export type PartsFinding = {
  classification?: string;
  sourceText?: string;
  proposedScope?: string;
  manHours?: number | null;
  manHoursSource?: string;
  parts?: ProposedPart[];
};

const logbook = /\blog\s*book\b/i;
function missingLogbookOnly(finding: PartsFinding) {
  if (finding.classification !== "missing-documentation") return false;
  const source = finding.sourceText ?? "";
  const absent = /\blog\s*book\s*(?:present|available)\s*[:?=-]?\s*(?:\([ny]\)\s*)?no\b/i.test(source) ||
    /\b(?:no|missing|absent)\s+(?:(?:crane|inspection)\s+)?log\s*book\b/i.test(source) ||
    /\blog\s*book\s+(?:is\s+|was\s+)?(?:missing|absent|not\s+(?:present|available))\b/i.test(source);
  // Do not apply a zero-labour rule to a combined finding about other work.
  return absent && !/\b(?:brake|wheel|motor|shackle|repair|adjust)\b/i.test(source) &&
    source.split(/[;.\n]+/).filter((part) => part.trim()).every((part) => logbook.test(part) || /^\s*(?:no|yes)\s*$/i.test(part));
}

const explicitComponents = [
  { name: "Shackle", pattern: /\bshackles?\b/i },
  { name: "Brake pads", pattern: /\bbrake\s+pads?\b/i },
  { name: "Ratchet disc", pattern: /\bratchet\s+discs?\b/i },
] as const;

const replacementAction = /\b(?:replace|replacement|renew|install)\b/i;
const alternativeAction = /\b(?:repair\s+or\s+replace|replace\s+or\s+repair)\b/i;

export function completeExplicitPartProposals<T extends PartsFinding>(finding: T): T & { parts: ProposedPart[]; partsReviewRequired: boolean } {
  const parts = [...(Array.isArray(finding.parts) ? finding.parts : [])];
  if (missingLogbookOnly(finding)) {
    if (!parts.some((part) => logbook.test(part.description ?? ""))) {
      parts.push({ description: "Logbook", quantity: "", source: "missing-logbook baseline proposal", suggestedSourceType: "manual-review", suggestedSourceName: "" });
    }
    // Preserve explicit technician labour for human review; replace only the AI/default estimate.
    return { ...finding, proposedScope: "Supply a replacement logbook.",
      manHours: finding.manHoursSource === "technician" ? finding.manHours : 0,
      manHoursSource: finding.manHoursSource === "technician" ? "technician" : "ai-proposed",
      parts, partsReviewRequired: false };
  }
  const scope = finding.proposedScope ?? "";
  const activeRepair = finding.classification === "repair-required" || finding.classification === "failed-check";
  const replacement = activeRepair && replacementAction.test(scope);

  // Only fill named, unambiguous replacement items. All specifications and suppliers remain for human review.
  if (replacement && !alternativeAction.test(scope)) {
    for (const component of explicitComponents) {
      if (!component.pattern.test(scope) || parts.some((part) => component.pattern.test(part.description ?? ""))) continue;
      parts.push({ description: component.name, quantity: "", source: "scope-derived proposal", suggestedSourceType: "manual-review", suggestedSourceName: "" });
    }
  }

  return { ...finding, parts, partsReviewRequired: Boolean(replacement && parts.length === 0) };
}

export function partRoutingGuidance(description: string) {
  if (/\bshackles?\b/i.test(description)) return "General hardware: consider an approved local general parts supplier. Confirm the required rating and permanent-use specification.";
  if (/\b(?:brake\s+pads?|ratchet\s+discs?)\b/i.test(description)) return "Equipment-specific part: confirm model compatibility and the actual supplying company.";
  return "";
}
